import classNames from 'classnames'
import { useRouter } from 'next/router'
import type { FunctionComponent, ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { DevMenu } from '../dev-menu'
import type { DisclaimersProps } from '../disclaimers/disclaimers'
import { ObservedModal } from '../modal'
import { ObservedSnackbar } from '../snackbar'
import { Footer } from './footer'
import { Header } from './header'
import { HeaderBanner } from './header-banner'
import styles from './layout.module.scss'
import type { BannerFragment } from '@graphql/fragments/BannerFragment.graphql'
import type { ThemeColorFragment } from '@graphql/fragments/ThemeColorFragment.graphql'
import { useDidMount } from '@lib/hooks/useDidMount'
import { useUIStore } from '@lib/store/useUIStore'
import { withTheme } from '@lib/utils/withTheme'
import { isPrd } from '@shared/util/env'
import { useIsAppPage } from '@shared/util/link'

/** Main website content container. Includes header and footer. */
export const Layout: FunctionComponent<Props> = ({
    children,
    disclaimers,
    theme,
    asPath,
    announcement,
}) => {
    const mounted = useDidMount()
    const { webview } = useUIStore()
    const router = useRouter()
    const [showBanner, setShowBanner] = useState(!!announcement)
    const hasBanner = !!announcement && showBanner

    // Check for exact '/earlypay' or paths that start with '/earlypay/'
    // const currentPath = asPath || router.asPath || router.pathname
    // const isPlaybook = currentPath && /^\/earlypay(\/|$)/.test(currentPath)
    const isPlaybook =
        router.pathname === '/earlypay' ||
        router.pathname.startsWith('/earlypay/') ||
        (router.asPath && /^\/earlypay(\/|$)/.test(router.asPath))

    // once the router is ready, it will include the path that accounts for rewrites.
    // this is useful because during pre-rendering, "asPath" is not the rewritten path.
    const renderedInsideApp = useIsAppPage(asPath)

    const landingPagePaths = ['/earlypay-signup']

    const isEarlyPayLandingPage = landingPagePaths.some((path) =>
        router.asPath.includes(path),
    )

    useEffect(() => {
        webview.setIsWebview(renderedInsideApp)
        if (renderedInsideApp) {
            try {
                if (
                    typeof window !== 'undefined' &&
                    window.Cookiebot &&
                    typeof window.Cookiebot.submitCustomConsent ===
                        'function' &&
                    typeof window.Cookiebot.hide === 'function'
                ) {
                    window.Cookiebot.submitCustomConsent(true, true, true)
                    window.Cookiebot.hide()
                } else {
                    console.warn('Cookiebot is undefined')
                }
            } catch (error) {
                console.error('Cookiebot error:', error)
            }
        }
    }, [renderedInsideApp, webview])

    useEffect(() => {
        const handleViewportChange = () => {
            if (window.visualViewport) {
                const keyboardHeight =
                    window.innerHeight - window.visualViewport.height
                document.documentElement.style.setProperty(
                    '--keyboard-height',
                    `${Math.max(0, keyboardHeight)}px`,
                )
            }
        }

        if (window.visualViewport) {
            window.visualViewport.addEventListener(
                'resize',
                handleViewportChange,
            )
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener(
                    'resize',
                    handleViewportChange,
                )
            }
        }
    }, [])

    return withTheme(
        theme?.key,
        <div
            className={classNames(
                styles.wrapper,
                isPlaybook && styles.playbookWrapper,
            )}
        >
            {!isPlaybook && hasBanner && (
                <HeaderBanner
                    {...announcement}
                    onClose={() => setShowBanner(false)}
                />
            )}

            {!isPlaybook && !renderedInsideApp && (
                <Header
                    hasBanner={hasBanner}
                    hideNavigation={isEarlyPayLandingPage}
                />
            )}

            <main
                className={classNames(
                    styles.main,
                    !isPlaybook && !renderedInsideApp && styles['with-header'],
                    isPlaybook && styles.playbookWrapper,
                )}
                data-loaded={mounted}
                data-theme={theme?.key}
            >
                {children}
            </main>

            {!isPlaybook && !renderedInsideApp && (
                <Footer
                    theme={theme?.key as string}
                    disclaimers={disclaimers}
                    showDisclaimersOnly={isEarlyPayLandingPage}
                />
            )}

            {!isPrd() && !isPlaybook && mounted && <DevMenu />}

            <>
                <ObservedModal />
                <ObservedSnackbar />
            </>
        </div>,
    )
}

type Props = {
    children: ReactNode
    className?: string
    asPath: string
    disclaimers?: DisclaimersProps
    theme: ThemeColorFragment
    announcement?: BannerFragment
}
